import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Grid, Tooltip } from '@material-ui/core';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CustomInput from 'components/CustomInput';
import { usePricingCalculatorStyles } from './PricingCalculatorStyles';
import { PRICING_MODES, COMPONENT_ORDER } from './PricingCalculatorConsts';
import { useGetClientsWithConfigsQuery } from 'features/clients/clientConfigSlice';
import { ActivityItemPricingType } from '../../../PricingManagementConsts';
import { IconDollar, IconPercent } from 'icons';

const round2 = (num) => Math.round(num * 100) / 100;

const calculateCharge = (costValue, marginValue) => {
    return round2(costValue + ((costValue * marginValue) / 100));
};

const calculateMargin = (costValue, chargeValue) => {
    if (costValue === 0) return 0;
    return round2(((chargeValue - costValue) / costValue) * 100);
};

const isEqualNumber = (a, b) => Math.abs(a - b) < 0.001;

const PricingCalculator = ({ clientId, value, onChange, pricingType, materials, isDefaultClientPricing = false }) => {
    const classes = usePricingCalculatorStyles();
    const userEditedRef = useRef(false);
    const [mode, setMode] = useState(value?.priceMode || PRICING_MODES.COST_PLUS.id);
    const [price, setPrice] = useState(value?.price || 0);
    const [margin, setMargin] = useState(value?.margin || 0);
    const [charge, setCharge] = useState(value?.charge || 0);
    const [materialPrices, setMaterialPrices] = useState(value?.materialPrices || []);
    const [clientConfig, setClientConfig] = useState();
    const { data: clientsWithConfigs } = useGetClientsWithConfigsQuery(null);

    const isBasePlusMaterials = pricingType === ActivityItemPricingType.BASE_PLUS_MATERIALS;

    const clientsById = useMemo(() => {
        if (!clientsWithConfigs) return {};

        return clientsWithConfigs.reduce((acc, client) => {
            acc[String(client.id)] = client;
            return acc;
        }, {});
    }, [clientsWithConfigs]);

    useEffect(() => {
        if (!isBasePlusMaterials || !materials?.length) {
            if (materialPrices.length > 0) setMaterialPrices([]);
            return;
        }

        setMaterialPrices(prev => {
            const activeMaterials = materials;

            const previousMaterialsById = prev.reduce((acc, materialPrice) => {
                if (materialPrice.activityItemMaterialId) {
                    acc[materialPrice.activityItemMaterialId] = materialPrice;
                }

                return acc;
            }, {});

            const newPrices = activeMaterials.map((m, index) => {
                const existing = m.id
                    ? previousMaterialsById[m.id]
                    : prev.find(p => !p.activityItemMaterialId && p.materialName === m.materialName);

                return {
                    materialIndex: index,
                    materialName: m.materialName,
                    unitOfMeasure: m.unitOfMeasure || '',
                    cost: existing?.cost ?? m.cost ?? 0,
                    activityItemMaterialId: m.id || null,
                    activityItemMaterialPriceId: m.activityItemMaterialPriceId || 0,
                };
            });

            if (prev.length === newPrices.length &&
                newPrices.every((np, i) =>
                    prev[i].materialIndex === np.materialIndex &&
                    prev[i].materialName === np.materialName &&
                    prev[i].unitOfMeasure === np.unitOfMeasure &&
                    prev[i].activityItemMaterialId === np.activityItemMaterialId &&
                    prev[i].cost === np.cost
                )) {
                return prev;
            }

            return newPrices;
        });
    }, [materials, isBasePlusMaterials]);

    useEffect(() => {
        if (!clientId) {
            setClientConfig(undefined);
            return;
        }

        setClientConfig(clientsById[String(clientId)]);
    }, [clientId, clientsById]);

    const materialsCostTotal = useMemo(() => {
        if (!isBasePlusMaterials) return 0;
        return materialPrices.reduce((sum, m) => sum + (parseFloat(m.cost) || 0), 0);
    }, [materialPrices, isBasePlusMaterials]);

    const totalCost = useMemo(() => {
        return (parseFloat(price) || 0) + materialsCostTotal;
    }, [price, materialsCostTotal]);

    useEffect(() => {
        if (!userEditedRef.current) return;
        const result = { price, margin, charge, priceMode: mode, materialPrices: isBasePlusMaterials ? materialPrices : [], totalCost };
        onChange?.(result);
    }, [price, margin, charge, mode, materialPrices, totalCost, isBasePlusMaterials]);


    useEffect(() => {
        const costBase = parseFloat(price) || 0;

        if (mode === PRICING_MODES.COST_PLUS.id) {
            const marginValue = parseFloat(clientConfig?.config?.mechanicalsConfig?.margin) || 0;
            const calculatedCharge = calculateCharge(costBase, marginValue);

            if (!isEqualNumber(margin, marginValue)) {
                setMargin(marginValue);
            }

            if (!isEqualNumber(charge, calculatedCharge)) {
                setCharge(calculatedCharge);
            }
        }
        else if (mode === PRICING_MODES.CLIENT_PRICE.id) {
            const chargeValue = parseFloat(charge) || 0;
            const calculatedMargin = calculateMargin(costBase, chargeValue);

            if (!isEqualNumber(margin, calculatedMargin)) {
                setMargin(calculatedMargin);
            }
        }
    }, [mode, price, charge, clientConfig, margin, totalCost, isBasePlusMaterials]);

    const handleMaterialCostChange = (index, val) => {
        userEditedRef.current = true;
        setMaterialPrices(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], cost: parseFloat(val) || 0 };
            return updated;
        });
    };

    const pricingComponents = {
        margin: {
            component: (
                <Grid item className={`${classes.inputItem} ${classes.marginInput}`}>
                    <Tooltip title={mode === PRICING_MODES.CLIENT_PRICE.id ? "Auto-calculated in Client-Price mode" : "From clients SLA"} placement="top-start" arrow disableInteractive>
                        <div>
                            <div className={classes.inputLabel}>Margin</div>
                            <CustomInput
                                id="margin"
                                label=""
                                value={margin}
                                elementType="input"
                                numberOnly
                                showEmpty
                                disabled={true}
                                icon={<IconPercent />}
                            />
                        </div>
                    </Tooltip>
                </Grid>
            ),
            separator: '↓'
        },
        charge: {
            component: (
                <Grid item className={`${classes.inputItem} ${classes.chargeInput}`}>
                    <Tooltip title={mode === PRICING_MODES.COST_PLUS.id ? "Auto-calculated in Cost-Plus mode" : ""} placement="top-start" arrow disableInteractive>
                        <div>
                            <div className={classes.inputLabel}>
                                Client Price
                            </div>
                            <CustomInput
                                id="charge"
                                label=""
                                value={charge}
                                onChange={(val) => { userEditedRef.current = true; setCharge(parseFloat(val) || 0); }}
                                elementType="input"
                                numberOnly
                                showEmpty
                                disabled={mode === PRICING_MODES.COST_PLUS.id}
                                icon={<IconDollar />}
                            />
                        </div>
                    </Tooltip>
                </Grid>
            ),
            separator: '↓'
        }
    };

    const componentOrder = COMPONENT_ORDER[mode];
    const visibleComponentOrder = isDefaultClientPricing
        ? componentOrder.filter(key => key !== 'margin' && key !== 'charge')
        : componentOrder;

    return (
        <Grid container direction="column">
            <Grid item className={classes.header}>
                <ToggleButtonGroup
                    value={mode}
                    exclusive
                    disabled={isDefaultClientPricing}
                    onChange={(event, newMode) => {
                        if (!newMode) return;
                        userEditedRef.current = true;
                        setMode(newMode);
                    }}
                    aria-label="calculation mode"
                    size="small"
                    className={classes.toggleButtonGroup}
                >
                    <ToggleButton value={PRICING_MODES.COST_PLUS.id}>
                        {PRICING_MODES.COST_PLUS.name}
                    </ToggleButton>
                    <ToggleButton value={PRICING_MODES.CLIENT_PRICE.id}>
                        {PRICING_MODES.CLIENT_PRICE.name}
                    </ToggleButton>
                </ToggleButtonGroup>
            </Grid>

            <Grid item className={`${classes.inputItem} ${classes.priceInput}`}>
                <div className={classes.inputLabel}>
                    {isBasePlusMaterials ? 'Base Cost' : 'Internal Cost'}
                </div>
                <CustomInput
                    id="price"
                    value={price === 0 ? '' : price}
                    onChange={(val) => { userEditedRef.current = true; setPrice(parseFloat(val) || 0); }}
                    elementType="input"
                    numberOnly
                    showEmpty
                    icon={<IconDollar />}
                />
            </Grid>

            {visibleComponentOrder.length > 0 && (
                <>
                    <div className={classes.separator}>↓</div>

                    {visibleComponentOrder.map((key, index) => (
                        <React.Fragment key={key}>
                            {pricingComponents[key].component}
                            {index < visibleComponentOrder.length - 1 && (
                                <div className={classes.separator}>{pricingComponents[key].separator}</div>
                            )}
                        </React.Fragment>
                    ))}
                </>
            )}

            {isBasePlusMaterials && materialPrices.length > 0 && (
                <>
                    <div className={classes.materialsSectionWrapper}>
                        <div className={classes.materialsSection}>
                            <div className={classes.materialsSectionHeader}>Materials</div>
                            {materialPrices.map((material, index) => {
                                const materialKey = material.activityItemMaterialId
                                    ? `material-${material.activityItemMaterialId}`
                                    : `material-new-${material.materialName}-${index}`;

                                return (
                                    <Grid container key={materialKey} alignItems="center" className={classes.materialRow}>
                                        <Grid item xs={7}>
                                            <div className={classes.materialName}>
                                                {material.materialName}
                                                {material.unitOfMeasure ? ` (${material.unitOfMeasure})` : ''}
                                            </div>
                                        </Grid>
                                        <Grid item xs={5}>
                                            <div className={classes.materialCostLabel}>Unit Cost</div>
                                            <CustomInput
                                                id={`material-cost-${index}`}
                                                value={material.cost === 0 ? '' : material.cost}
                                                onChange={(val) => handleMaterialCostChange(index, val)}
                                                elementType="input"
                                                numberOnly
                                                showEmpty
                                                icon={<IconDollar />}
                                            />
                                        </Grid>
                                    </Grid>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}
        </Grid>
    );
};

export default PricingCalculator;
